import { useCallback, useRef } from "react";
import { API, http } from "@/lib/api";

const SPEECH_FACTOR = 3;
const MIN_LEVEL = 0.012;

export const useHandsFree = () => {
  const audio = useRef(null);
  const stream = useRef(null);
  const ctx = useRef(null);
  const abort = useRef(null);

  const unlock = useCallback(async () => {
    audio.current = audio.current || new Audio();
    audio.current.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";
    await audio.current.play().catch(() => {});
    stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    ctx.current = new (window.AudioContext || window.webkitAudioContext)();
    await ctx.current.resume();
  }, []);

  const speak = useCallback(async (text) => {
    let cancelled = false;
    abort.current = () => { cancelled = true; };
    const { data } = await http.post("/tts", { text });
    if (cancelled) { abort.current = null; return; }
    const el = audio.current;
    el.src = `${API.replace(/\/api$/, "")}${data.url}`;
    await new Promise((resolve, reject) => {
      const done = () => { el.onended = el.onerror = null; resolve(); };
      abort.current = () => { el.pause(); done(); };
      el.onended = done;
      el.onerror = () => reject(new Error("Audio playback failed"));
      el.play().catch(reject);
    });
    abort.current = null;
  }, []);

  const beep = useCallback((freq = 880, ms = 160) => {
    const c = ctx.current;
    if (!c) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.frequency.value = freq;
    g.gain.value = 0.15;
    o.connect(g).connect(c.destination);
    o.start();
    o.stop(c.currentTime + ms / 1000);
  }, []);

  const listen = useCallback(({ maxMs, silenceMs, onLevel, onTick }) => new Promise((resolve) => {
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t));
    const rec = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    const src = ctx.current.createMediaStreamSource(stream.current);
    const an = ctx.current.createAnalyser();
    an.fftSize = 1024;
    src.connect(an);
    const buf = new Float32Array(an.fftSize);
    const t0 = Date.now();
    let floor = null;
    let spoke = false;
    let lastVoice = Date.now();
    let finished = false;    const finish = (reason) => {
      if (finished) return;
      finished = true;
      clearInterval(timer);
      src.disconnect();
      rec.onstop = () => resolve({ blob: new Blob(chunks, { type: rec.mimeType || "audio/webm" }), reason, spoke });
      rec.state !== "inactive" ? rec.stop() : rec.onstop();
    };
    abort.current = () => finish("manual");
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.start(500);
    const timer = setInterval(() => {
      an.getFloatTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((a, v) => a + v * v, 0) / buf.length);
      const elapsed = Date.now() - t0;
      floor = floor == null || rms < floor ? rms : floor * 1.003;
      const threshold = Math.max(MIN_LEVEL, floor * SPEECH_FACTOR);
      if (rms > threshold) { spoke = true; lastVoice = Date.now(); }
      onLevel?.(Math.min(1, rms / (threshold * 3)));
      onTick?.(elapsed);
      if (spoke && Date.now() - lastVoice > silenceMs) finish("silence");
      if (elapsed > maxMs) finish("timeout");
    }, 100);
  }), []);

  const interrupt = useCallback(() => abort.current?.(), []);

  const release = useCallback(() => {
    abort.current?.();
    audio.current?.pause();
    stream.current?.getTracks().forEach((t) => t.stop());
    ctx.current?.close().catch(() => {});
    stream.current = ctx.current = null;
  }, []);

  return { unlock, speak, beep, listen, interrupt, release };
};
